import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/auth-api";
import type {
	ClearancePriceResponse,
	TopResultDetailResponse,
	TopResultsQueryParams,
	TopResultsResponse,
	ZalegaczeDatasetResponse,
} from "./types";

const BASE = "/strateg-cenowy";
const PAGE_SIZE = 100;

/** Query key SSoT — invalidation po manual edit / AI apply MUSI uzywac tego klucza. */
export const TOP_RESULTS_QUERY_KEY = ["strateg-cenowy", "top", "results"] as const;

function buildResultsQs(params: TopResultsQueryParams, pageOffset: number): string {
	const sp = new URLSearchParams();
	sp.set("limit", String(params.limit ?? PAGE_SIZE));
	sp.set("offset", String(pageOffset));
	const f = params.filters;
	if (f) {
		if (f.modifierType) sp.set("modifierType", f.modifierType);
		if (f.fallback !== undefined) sp.set("fallback", f.fallback ? "true" : "false");
		if (f.highPotentialOnly) sp.set("highPotentialOnly", "true");
		if (f.search) sp.set("search", f.search);
	}
	return sp.toString();
}

export async function fetchTopResults(
	params: TopResultsQueryParams,
	pageOffset: number,
): Promise<TopResultsResponse> {
	const qs = buildResultsQs(params, pageOffset);
	return apiFetch<TopResultsResponse>(`${BASE}/results-top?${qs}`);
}

/**
 * Infinite query dla wynikow algorytmu TOP (T1/T2/T3). Cennik TOP jest
 * organization-wide — bez scope. PAGE_SIZE=100, `keepPreviousData` zapobiega
 * miganiu tabeli przy zmianie filtrow.
 */
export function useTopResultsInfinite(
	params: Omit<TopResultsQueryParams, "limit" | "offset"> = {},
) {
	return useInfiniteQuery({
		queryKey: [...TOP_RESULTS_QUERY_KEY, "infinite", params] as const,
		queryFn: ({ pageParam = 0 }) => fetchTopResults({ ...params, limit: PAGE_SIZE }, pageParam),
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			const loaded = allPages.reduce((sum, p) => sum + p.items.length, 0);
			return loaded < lastPage.total ? loaded : undefined;
		},
		placeholderData: keepPreviousData,
	});
}

/**
 * Single-page query — uzywany w `WynikiTopPage.tsx` (tabela bez infinite scroll
 * na razie). Limit 5000 = backend hard cap (`MAX_EXPORT_ROWS` w `export_top.rs`).
 * Typowy IQVIA decyl ma ~1.5-2k pozycji, wiec 5k z duzym zapasem mieszcza caly
 * cennik. Jezeli organizacja przekroczy 5k pozycji → switch do `useTopResultsInfinite`.
 */
export function useTopResults(params: TopResultsQueryParams = {}, enabled = true) {
	return useQuery({
		queryKey: [...TOP_RESULTS_QUERY_KEY, "page", params] as const,
		queryFn: () => fetchTopResults({ limit: 5000, ...params }, params.offset ?? 0),
		// 5 minut — TOP cennik to dane uploadowane raz/tydzien (NIE real-time).
		// Mutacje (manual edit, AI apply/revert) i tak invalidate'uja cache.
		// Zgodnie z `project_query_client_defaults.md` (v246) dla MV-based queries.
		staleTime: 5 * 60_000,
		enabled,
	});
}

export async function fetchTopResultDetail(bloz07: string): Promise<TopResultDetailResponse> {
	return apiFetch<TopResultDetailResponse>(`${BASE}/results-top/${encodeURIComponent(bloz07)}`);
}

export function useTopDetail(bloz07: string) {
	return useQuery({
		queryKey: ["strateg-cenowy", "top", "detail", bloz07] as const,
		queryFn: () => fetchTopResultDetail(bloz07),
		enabled: !!bloz07,
	});
}

// ─── Mutations: manual edit + AI apply/revert ──────────────────────────────

export type PatchTopField = "top1" | "top2" | "top3";

export interface PatchTopInput {
	bloz07: string;
	field: PatchTopField;
	value: number;
}

/**
 * Splatch zaktualizowany row w cache zamiast invalidate'owac calą tabele.
 * Backend zwraca pelny `TopResultDetailResponse extends TopResultRow` z PATCH/
 * apply/revert — wystarczy dopasowac po `bloz07` PK i wymienic in-place.
 *
 * Z `setQueriesData` z `TOP_RESULTS_QUERY_KEY` jako prefix kluczy splatch dotyka
 * wszystkich cached pages (page mode + infinite mode). Dla infinite trzeba
 * mapowac `pages: [{items, total}, ...]` zamiast plaskie items.
 */
function applyUpdatedRowToCache(
	qc: ReturnType<typeof useQueryClient>,
	updated: TopResultDetailResponse,
) {
	qc.setQueriesData<TopResultsResponse | { pages: TopResultsResponse[]; pageParams: unknown[] }>(
		{ queryKey: TOP_RESULTS_QUERY_KEY },
		(old) => {
			if (!old) return old;
			// page mode (`useTopResults`)
			if ("items" in old) {
				return {
					...old,
					items: old.items.map((row) =>
						row.bloz07 === updated.bloz07 ? { ...row, ...updated } : row,
					),
				};
			}
			// infinite mode (`useTopResultsInfinite`) — splatch w każdej page.
			return {
				...old,
				pages: old.pages.map((page) => ({
					...page,
					items: page.items.map((row) =>
						row.bloz07 === updated.bloz07 ? { ...row, ...updated } : row,
					),
				})),
			};
		},
	);
}

/**
 * Manual edit pojedynczego pola TOP1/TOP2/TOP3. Pole `top2` triggeruje
 * backend-side recalc top1+top3 z `pricing_config_top.margin_settings`.
 * Pola `top1`/`top3` zostawiaja inne nietkniete.
 *
 * Backend: PATCH /strateg-cenowy/results-top/{bloz07}.
 * Po sukcesie SPLATCH'uje row w cache (optimistic), nie invaliduje calej tabeli
 * — zapobiega refetch'owi 500-5000 wierszy po pojedynczej edycji. Fallback:
 * invalidate na error (refresh na wypadek desync cache).
 */
export function useUpdateTopResultMutation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ bloz07, field, value }: PatchTopInput) =>
			apiFetch<TopResultDetailResponse>(`${BASE}/results-top/${encodeURIComponent(bloz07)}`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ field, value }),
			}),
		onSuccess: (updated) => applyUpdatedRowToCache(qc, updated),
		onError: () => {
			qc.invalidateQueries({ queryKey: TOP_RESULTS_QUERY_KEY });
		},
	});
}

/**
 * Przyjmij AI sugestie jako TOP2. Backend wylicza TOP1/TOP3 z recalc, zapisuje
 * oldTop snapshot (sticky COALESCE — pierwsza edycja zachowana).
 */
export function useApplyAiTopMutation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (bloz07: string) =>
			apiFetch<TopResultDetailResponse>(
				`${BASE}/results-top/${encodeURIComponent(bloz07)}/apply-ai`,
				{ method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
			),
		onSuccess: (updated) => applyUpdatedRowToCache(qc, updated),
		onError: () => {
			qc.invalidateQueries({ queryKey: TOP_RESULTS_QUERY_KEY });
		},
	});
}

export interface ApplyAiAllResponse {
	applied: number;
	skippedManual: number;
	totalWithAi: number;
}

/**
 * Masowe "Przyjmij wszystkie sugestie AI" — backend robi apply-ai dla każdej
 * pozycji z sugestią z aktywnego batcha w jednej transakcji (manual edity
 * pomijane). Po sukcesie pełny invalidate — zmienia się większość wierszy.
 */
export function useApplyAiAllTopMutation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: () =>
			apiFetch<ApplyAiAllResponse>(`${BASE}/results-top/apply-ai-all`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: "{}",
			}),
		onSettled: () => {
			qc.invalidateQueries({ queryKey: TOP_RESULTS_QUERY_KEY });
		},
	});
}

// ─── Cena wyprzedażowa (zalegacze własne, atrybut 3003) ─────────────────────

export interface SetClearancePriceInput {
	bloz07: string;
	price: number;
}

export const ZALEGACZE_DATASET_QUERY_KEY = ["strateg-cenowy", "top", "zalegacze-dataset"] as const;

export function useZalegaczeDataset(minQty30: number, enabled = true) {
	return useQuery({
		queryKey: [...ZALEGACZE_DATASET_QUERY_KEY, minQty30] as const,
		queryFn: () =>
			apiFetch<ZalegaczeDatasetResponse>(
				`${BASE}/results-top/zalegacze-wlasne/list?minQty30=${minQty30}`,
			),
		enabled,
		staleTime: 5 * 60_000,
	});
}

/**
 * Ustaw jedną cenę wyprzedażową dla zalegacza własnego. Persystencja w
 * `strateg_clearance_prices` — NIEZALEŻNA od overrides TOP (revert-ai jej nie
 * rusza). Backend waliduje 0.01..9999.99 + aktualną kwalifikację 3003.
 */
export function useSetClearancePriceMutation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ bloz07, price }: SetClearancePriceInput) =>
			apiFetch<ClearancePriceResponse>(
				`${BASE}/results-top/zalegacze-wlasne/${encodeURIComponent(bloz07)}/price`,
				{
					method: "PATCH",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ price }),
				},
			),
		onSettled: () => {
			qc.invalidateQueries({ queryKey: ZALEGACZE_DATASET_QUERY_KEY });
			qc.invalidateQueries({ queryKey: CLEARANCE_EXPORT_COUNT_KEY });
			qc.invalidateQueries({ queryKey: TOP_RESULTS_QUERY_KEY });
		},
	});
}

/** Wyczyść cenę wyprzedażową (DELETE, idempotentne). Zero NIGDY nie jest "brak". */
export function useClearClearancePriceMutation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (bloz07: string) =>
			apiFetch<ClearancePriceResponse>(
				`${BASE}/results-top/zalegacze-wlasne/${encodeURIComponent(bloz07)}/price`,
				{ method: "DELETE" },
			),
		onSettled: () => {
			qc.invalidateQueries({ queryKey: ZALEGACZE_DATASET_QUERY_KEY });
			qc.invalidateQueries({ queryKey: CLEARANCE_EXPORT_COUNT_KEY });
			qc.invalidateQueries({ queryKey: TOP_RESULTS_QUERY_KEY });
		},
	});
}

export interface BulkClearanceDiscountResponse {
	applied: number;
	skippedWithoutAverage: number;
}

/** Ustaw ceny dla całego datasetu 3003, niezależnie od filtrów tabeli. */
export function useBulkClearanceDiscountMutation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (discountPct: number) =>
			apiFetch<BulkClearanceDiscountResponse>(
				`${BASE}/results-top/zalegacze-wlasne/bulk-discount`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ discountPct }),
				},
			),
		onSettled: () => {
			qc.invalidateQueries({ queryKey: ZALEGACZE_DATASET_QUERY_KEY });
			qc.invalidateQueries({ queryKey: CLEARANCE_EXPORT_COUNT_KEY });
			qc.invalidateQueries({ queryKey: TOP_RESULTS_QUERY_KEY });
		},
	});
}

/** Jedna apteka własna z zalegającą partią BLOZ (panel boczny zalegaczy). */
export interface ZalegaczPharmacyRow {
	idfirm: number;
	unitname: string | null;
	maxDays: number | null;
	oldestPurchaseDate: string | null;
	staleUnits: number | null;
	/** Sztuki wstrzymane do sprzedaży (wszystkie partie BLOZ w aptece). */
	heldUnits: number | null;
	stockQty: number | null;
	stockValueNet: number | null;
}

/**
 * Rozbicie zalegacza per apteka — lazy loading: query odpala się dopiero po
 * otwarciu panelu (enabled). Backend zwraca najgorsze (najstarsze) pierwsze.
 */
export function useZalegaczPharmacies(bloz07: string | null) {
	return useQuery({
		queryKey: ["strateg-cenowy", "top", "zalegacz-pharmacies", bloz07] as const,
		queryFn: () =>
			apiFetch<ZalegaczPharmacyRow[]>(
				`${BASE}/results-top/zalegacze-wlasne/${encodeURIComponent(bloz07 ?? "")}/pharmacies`,
			),
		enabled: !!bloz07,
		staleTime: 5 * 60_000,
	});
}

export const CLEARANCE_EXPORT_COUNT_KEY = [
	"strateg-cenowy",
	"top",
	"clearance-export-count",
] as const;

/**
 * Liczba pozycji eksportowalnych (aktualne 3003 ∩ cena NOT NULL) — selekcja
 * serwerowa, żeby filtry DataTable nie mogły cicho zmienić cennika.
 */
export function useClearanceExportCount(enabled: boolean) {
	return useQuery({
		queryKey: CLEARANCE_EXPORT_COUNT_KEY,
		queryFn: () => apiFetch<{ count: number }>(`${BASE}/results-top/zalegacze-wlasne/export-count`),
		enabled,
		staleTime: 60_000,
	});
}

/**
 * Cofnij AI/manual apply — przywroc oldTop snapshot. Backend: jezeli
 * old_applied_operation == 'computed', DELETE override (powrot do czystego
 * computed). Inaczej restore w UPDATE.
 */
export function useRevertAiTopMutation() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (bloz07: string) =>
			apiFetch<TopResultDetailResponse>(
				`${BASE}/results-top/${encodeURIComponent(bloz07)}/revert-ai`,
				{ method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" },
			),
		onSuccess: (updated) => applyUpdatedRowToCache(qc, updated),
		onError: () => {
			qc.invalidateQueries({ queryKey: TOP_RESULTS_QUERY_KEY });
		},
	});
}
